import { DOCUMENT, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import {
  DEFAULT_CURRENCY_CODE,
  inject,
  Injectable,
  LOCALE_ID,
  provideAppInitializer,
  type EnvironmentProviders,
  type Provider,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { WORKFORCE_LANGS, type WorkforceLang } from '../i18n/workforce-langs';

const ANGULAR_LOCALE_BY_LANG: Record<WorkforceLang, string> = {
  en: 'en-US',
  de: 'de',
  it: 'it',
  nl: 'nl',
};

const CURRENCY_BY_LANG: Record<WorkforceLang, string> = {
  en: 'USD',
  de: 'EUR',
  it: 'EUR',
  nl: 'EUR',
};

/** Resolves Angular `LOCALE_ID` from the active Transloco language. */
@Injectable({ providedIn: 'root' })
export class WorkforceLocaleId {
  private readonly transloco = inject(TranslocoService);

  toString(): string {
    const lang = this.transloco.getActiveLang() as WorkforceLang;
    return ANGULAR_LOCALE_BY_LANG[lang] ?? ANGULAR_LOCALE_BY_LANG.en;
  }

  valueOf(): string {
    return this.toString();
  }
}

/** Resolves default currency from the active Transloco language. */
@Injectable({ providedIn: 'root' })
export class WorkforceCurrencyCode {
  private readonly transloco = inject(TranslocoService);

  toString(): string {
    const lang = this.transloco.getActiveLang() as WorkforceLang;
    return CURRENCY_BY_LANG[lang] ?? CURRENCY_BY_LANG.en;
  }

  valueOf(): string {
    return this.toString();
  }
}

/**
 * Register locale data before `bootstrapApplication`.
 * Required for `DatePipe`, `CurrencyPipe`, and other locale-aware APIs.
 */
export function registerWorkforceLocaleData(): void {
  registerLocaleData(localeEn);
  registerLocaleData(localeDe);
  registerLocaleData(localeIt);
  registerLocaleData(localeNl);
}

export function provideWorkforceAppLocale(): Array<
  Provider | EnvironmentProviders
> {
  return [
    WorkforceLocaleId,
    WorkforceCurrencyCode,
    // DatePipe and Intl APIs expect a string locale id, not an object with
    // toString(). useClass caused "locale.toLowerCase is not a function".
    {
      provide: LOCALE_ID,
      useFactory: () => inject(WorkforceLocaleId).toString(),
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useFactory: () => inject(WorkforceCurrencyCode).toString(),
    },
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      const doc = inject(DOCUMENT);

      const syncDocumentLang = (lang: string) => {
        if (WORKFORCE_LANGS.includes(lang as WorkforceLang)) {
          doc.documentElement.lang = lang;
        }
      };

      syncDocumentLang(transloco.getActiveLang() || transloco.getDefaultLang());
      transloco.langChanges$.subscribe(syncDocumentLang);
    }),
  ];
}
